import { z } from 'zod';
import { LOGO_MAX_ZEICHEN } from '../constants.js';

/** Erscheinungsbild eines Templates — Farben, Logo, Kopf- und Fußzeile. */

const farbeSchema = z
  .string()
  .regex(
    /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
    'Farbe muss als Hex-Wert angegeben werden, z. B. #1d4ed8',
  );

/**
 * Logo als Data-URI.
 *
 * Zwei Gründe für die strenge Prüfung: Der Wert landet im Layout direkt in
 * einem `src`-Attribut — alles außer `data:image/png` und `data:image/jpeg`
 * hätte dort nichts zu suchen. Und die Länge begrenzt den Datensatz, in dem das
 * Logo mitgespeichert wird (siehe LOGO_MAX_ZEICHEN).
 */
export const logoDataUriSchema = z
  .string()
  .max(LOGO_MAX_ZEICHEN, 'Das Logo ist zu groß. Bitte verwenden Sie ein kleineres Bild.')
  .regex(
    /^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/,
    'Das Logo muss ein PNG oder JPEG sein.',
  );

export const brandingSchema = z.object({
  /** S3-Key des Logos unter `templates/`. Leer = kein Logo. */
  logoKey: z.string().max(500).optional(),
  /** Wird beim Rendern eingebettet, damit das PDF autark bleibt. */
  logoDataUri: logoDataUriSchema.optional(),
  primaerfarbe: farbeSchema.default('#1d4ed8'),
  textfarbe: farbeSchema.default('#111827'),
  akzentfarbe: farbeSchema.default('#e5e7eb'),
  schriftfamilie: z.string().max(200).default('"Source Sans 3", Arial, sans-serif'),
  schriftgroessePt: z.number().min(6).max(16).default(10),
  seitenrandMm: z.number().min(5).max(40).default(18),
  kopfzeile: z.string().max(2000).default(''),
  fusszeile: z.string().max(2000).default(''),
  firmenname: z.string().max(200).default(''),
  firmenanschrift: z.string().max(500).default(''),
  /** Fußzeile mit Protokollnummer, Seitenzahl, Version, Hash und QR-Code. */
  qrCodeAnzeigen: z.boolean().default(true),
  hashAnzeigen: z.boolean().default(true),
});

export type Branding = z.infer<typeof brandingSchema>;

export const STANDARD_BRANDING: Branding = brandingSchema.parse({});

/**
 * Liest gespeichertes Branding — aus einem JSON-Feld, das je nach Herkunft
 * schon ein Objekt oder noch eine Zeichenkette ist.
 *
 * Bewusst tolerant: Ein unbrauchbares Logo (etwa aus einer älteren, laxeren
 * Prüfung) darf nicht dazu führen, dass eine Vorlage gar nicht mehr geladen
 * werden kann. Dann fällt nur das Logo weg, und zwar sichtbar — nicht das
 * ganze Erscheinungsbild.
 */
export const brandingLesen = (rohwert: unknown): Branding => {
  let wert = rohwert;
  if (typeof wert === 'string') {
    try {
      wert = JSON.parse(wert);
    } catch {
      return STANDARD_BRANDING;
    }
  }
  if (wert === null || wert === undefined) return STANDARD_BRANDING;

  const geprueft = brandingSchema.safeParse(wert);
  if (geprueft.success) return geprueft.data;

  const ohneLogo = brandingSchema.safeParse({
    ...(typeof wert === 'object' ? wert : {}),
    logoDataUri: undefined,
  });
  return ohneLogo.success ? ohneLogo.data : STANDARD_BRANDING;
};
